import React from 'react';
import { X, Sparkles, Check, Clock } from 'lucide-react';
import { detectPricing } from '../lib/geo';
import { useSubscription } from '../context/SubscriptionContext';

const PERKS = [
  'Unlimited Socratic Tutor sessions with your favorite coach',
  'Full "Secrets of Egypt" audiobook — all 7 chapters',
  'Yearly, monthly, weekly & daily Goal Setting',
  'Support Crew: invite grandparents to cheer',
  'Unlimited quizzes, every day',
];

const FEATURE_LABEL = {
  tutor: 'the Socratic Tutor',
  audiobook: 'the audiobook',
  goals: 'Goal Setting',
  crew: 'Extended Family invites',
  quiz: 'unlimited quizzes',
};

export const PaywallModal = () => {
  const { paywall, closePaywall } = useSubscription();
  if (!paywall.open) return null;
  const p = detectPricing();
  const featureName = FEATURE_LABEL[paywall.feature] || 'this feature';

  return (
    <div className="fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="paywall-modal">
      <div className="bg-white rounded-3xl w-full max-w-md my-8 shadow-squishyLg overflow-hidden">
        <div className="bg-gradient-to-br from-navy via-teal to-navy p-6 text-white relative">
          <button onClick={closePaywall} className="absolute top-3 right-3 text-white/80 hover:text-white" aria-label="Close" data-testid="paywall-close">
            <X className="w-5 h-5" />
          </button>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-navy font-heading font-bold text-[11px] uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> Premium
          </div>
          <h2 className="font-heading text-2xl font-bold leading-tight mt-3" data-testid="paywall-headline">Unlock Your Child's Full Potential</h2>
          <p className="text-white/85 text-sm mt-1">Get unlimited access to the Socratic Tutor, Audiobook, Goal Setting and Family features.</p>
          <p className="text-gold text-xs font-heading font-bold mt-2">You just tapped {featureName}.</p>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            {PERKS.map((perk) => (
              <div key={perk} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-teal/15 text-teal flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <span className="text-sm text-navy leading-snug">{perk}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="font-heading text-4xl font-bold text-navy" data-testid="paywall-price">{p.price}</div>
            <div className="text-navy/60 text-xs font-heading font-bold uppercase tracking-wider">per month</div>
          </div>

          <a href={p.stripeUrl} target="_blank" rel="noopener noreferrer"
            className="block w-full h-14 rounded-full bg-gold text-navy font-heading font-bold text-base shadow-gold text-center leading-[3.5rem] hover:scale-[1.02] transition-transform"
            data-testid="paywall-cta">
            Start Premium — {p.price}/month
          </a>
          <p className="text-center text-navy/60 text-xs">Cancel anytime. No commitment.</p>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-center text-xs text-navy/70">
              Already have the $99 bundle?{' '}
              <span className="text-teal font-heading font-bold">Log in with your purchase email</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrialBanner = () => {
  const { status } = useSubscription();
  if (!status.in_trial) return null;
  return (
    <div className="rounded-2xl bg-gradient-to-r from-gold/25 to-teal/25 border-2 border-gold px-4 py-2 flex items-center gap-2 text-sm" data-testid="trial-banner">
      <Clock className="w-4 h-4 text-gold shrink-0" />
      <span className="text-navy font-heading font-bold">
        {status.trial_days_left} day{status.trial_days_left === 1 ? '' : 's'} left in your free trial
      </span>
    </div>
  );
};
